/**
 * Admin → Graph Subscriptions.
 *
 * Backend: `/admin/graph-subscriptions` (app/routers/admin_graph_subscriptions.py).
 * Subscribes the platform to a user's Microsoft Graph mailbox so the listener
 * tracks inbox/sent items — `crm-tracking` for CRM workflows and auto-responses,
 * `deal-reply` for deal thread replies.
 *
 * Reads come straight from Mongo, so the grid renders even when the listener is
 * down. Creates and deletes PROXY through the listener, so they fail with a 502
 * ("listener error: …") or a 503 ("listener not configured") — those details are
 * surfaced verbatim because they say which half is broken.
 *
 * Deleting is by mailbox, not by subscription: it removes ALL subscriptions for
 * that user.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";

import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AddRounded from "@mui/icons-material/AddRounded";
import MarkEmailReadOutlined from "@mui/icons-material/MarkEmailReadOutlined";
import RefreshRounded from "@mui/icons-material/Refresh";
import { useSnackbar } from "notistack";

import { AdminGrid } from "../shared/adminGrid";
import SearchInput from "../../../components/inputs/SearchInput";
import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import {
  adminGraphSubscriptionsApi,
  GRAPH_SUBSCRIPTION_PURPOSES,
} from "../../../api/adminPlatformApi";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { AdminCard, AdminEmpty, AdminScreenShell } from "../shared/AdminScreenShell";
import { removeColumn } from "../shared/adminTableActions";

const errText = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

const emailOf = (row) => row?.userEmail || row?.user_email || "";

export default function AdminGraphSubscriptionsScreen() {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickFilter, setQuickFilter] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    userEmail: "",
    purpose: "crm-tracking",
    richData: false,
  });

  const load = useCallback(() => {
    setLoading(true);
    adminGraphSubscriptionsApi
      .list()
      .then(setRows)
      .catch((err) => {
        setRows([]);
        enqueueSnackbar(errText(err, "Couldn't load subscriptions"), {
          variant: "error",
        });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  const create = useCallback(() => {
    const email = form.userEmail.trim();
    if (!email) return;
    setBusy(true);
    adminGraphSubscriptionsApi
      .create({
        userEmail: email,
        purpose: form.purpose,
        richData: form.richData,
      })
      .then(() => {
        enqueueSnackbar(`Subscribed ${email}`, { variant: "success" });
        setAddOpen(false);
        setForm({ userEmail: "", purpose: "crm-tracking", richData: false });
        load();
      })
      .catch((err) =>
        enqueueSnackbar(errText(err, "Couldn't create the subscription"), {
          variant: "error",
        }),
      )
      .finally(() => setBusy(false));
  }, [form, enqueueSnackbar, load]);

  const remove = useCallback(
    (row) => {
      const email = emailOf(row);
      if (
        !window.confirm(
          `Delete ALL Graph subscriptions for ${email}? Tracking for that mailbox stops immediately.`,
        )
      )
        return;
      adminGraphSubscriptionsApi
        .remove(email)
        .then(() => {
          enqueueSnackbar(`Removed subscriptions for ${email}`, {
            variant: "success",
          });
          load();
        })
        .catch((err) =>
          enqueueSnackbar(errText(err, "Couldn't delete the subscription"), {
            variant: "error",
          }),
        );
    },
    [enqueueSnackbar, load],
  );

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Mailbox",
        colId: "userEmail",
        valueGetter: (p) => emailOf(p.data),
        minWidth: 260,
        flex: 1.4,
        sort: "asc",
      },
      {
        headerName: "Purpose",
        field: "purpose",
        width: 160,
        cellRenderer: (p) =>
          p.value ? (
            <Chip
              size="small"
              label={p.value}
              sx={{ height: 20, fontSize: 11, fontWeight: 600 }}
            />
          ) : (
            "—"
          ),
      },
      {
        headerName: "Status",
        field: "status",
        width: 130,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Subscription id",
        field: "subscriptionId",
        minWidth: 240,
        flex: 1,
        valueFormatter: (p) => p.value || "—",
      },
      {
        headerName: "Expires",
        field: "expirationDateTime",
        width: 175,
        valueFormatter: (p) => formatDateTime(p.value) || "—",
      },
      {
        headerName: "Updated",
        field: "updateDateTime",
        width: 175,
        valueFormatter: (p) => formatDateTime(p.value) || "—",
      },
      // The admin action-cell convention (ADMIN_UI.md): the control fills the
      // cell, square, so the whole cell is the hit target.
      removeColumn((p) => remove(p.data), {
        tooltip: "Delete all subscriptions for this mailbox",
      }),
    ],
    [remove],
  );

  return (
    <AdminScreenShell
      icon={<MarkEmailReadOutlined />}
      title="Graph Subscriptions"
      count={rows?.length}
      loading={loading}
      subtitle="Per-user Microsoft Graph mailbox subscriptions the listener uses for CRM tracking and deal-thread replies. Reads come from the database; creating and deleting goes through the listener service."
      actions={
        <>
          <SearchInput
            value={quickFilter}
            onChange={setQuickFilter}
            placeholder="Search mailboxes…"
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshRounded />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddRounded />}
            onClick={() => setAddOpen(true)}
          >
            Subscribe a mailbox
          </Button>
        </>
      }
    >
      <AdminCard dense>
        {rows && rows.length === 0 ? (
          <AdminEmpty>No managed subscriptions.</AdminEmpty>
        ) : (
          <AdminGrid rowCount={(rows || []).length} maxRows={12}>
            <AgGridReact
              rowData={rows || []}
              columnDefs={columnDefs}
              defaultColDef={{
                sortable: true,
                resizable: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
              }}
              getRowId={(p) =>
                String(p.data?._id || p.data?.subscriptionId || emailOf(p.data))
              }
              quickFilterText={quickFilter}
              loading={loading}
            />
          </AdminGrid>
        )}
      </AdminCard>

      <Dialog
        open={addOpen}
        onClose={busy ? undefined : () => setAddOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
          Subscribe a mailbox
        </DialogTitle>
        <DialogContent dividers>
          <Flx column g={2} sx={{ pt: 1 }}>
            <TextField
              size="small"
              label="User email"
              required
              value={form.userEmail}
              onChange={(e) =>
                setForm((p) => ({ ...p, userEmail: e.target.value }))
              }
              fullWidth
            />
            <TextField
              select
              size="small"
              label="Purpose"
              value={form.purpose}
              onChange={(e) => setForm((p) => ({ ...p, purpose: e.target.value }))}
              fullWidth
            >
              {GRAPH_SUBSCRIPTION_PURPOSES.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={form.richData}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, richData: e.target.checked }))
                  }
                />
              }
              label={
                <Txt sx={{ fontSize: 12.5 }}>
                  Rich data (include message bodies in the notification payload)
                </Txt>
              }
            />
          </Flx>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setAddOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={create}
            disabled={busy || !form.userEmail.trim()}
          >
            Subscribe
          </Button>
        </DialogActions>
      </Dialog>
    </AdminScreenShell>
  );
}
